import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { HandCoins, Pencil, Plus, Search, Smartphone, Trash2, Users, Wallet, X } from 'lucide-react'
import {
  customerPaymentSchema, customerSchema, formatDateTime, formatMoney, formatNumber,
  PAYMENT_METHODS, PERMISSIONS, toMajor, toMinor, type CustomerListItem,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, ConfirmDialog, Dialog, EmptyState, Input, Pagination,
  Select, Switch, Textarea, Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { customersApi, type CustomerDetail } from '../lib/customersApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

interface FormState {
  phone: string
  fullName: string
  email: string
  address: string
  notes: string
  openingBalance: string
  creditLimit: string
  isActive: boolean
}

const EMPTY: FormState = {
  phone: '', fullName: '', email: '', address: '', notes: '',
  openingBalance: '', creditLimit: '', isActive: true,
}

function CustomerFormDialog({
  open, onClose, customer,
}: { open: boolean; onClose: () => void; customer: CustomerDetail | null }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()
  const [form, setForm] = useState<FormState>(EMPTY)
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }))

  useEffect(() => {
    if (!open) return
    clear()
    setForm(
      customer
        ? {
            phone: customer.phone,
            fullName: customer.fullName,
            email: customer.email ?? '',
            address: customer.address ?? '',
            notes: customer.notes ?? '',
            openingBalance: customer.openingBalance ? String(toMajor(customer.openingBalance)) : '',
            creditLimit: customer.creditLimit ? String(toMajor(customer.creditLimit)) : '',
            isActive: customer.isActive,
          }
        : EMPTY,
    )
  }, [open, customer, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = validate(customerSchema, {
        phone: form.phone.trim(),
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        openingBalance: toMinor(Number(form.openingBalance) || 0),
        creditLimit: toMinor(Number(form.creditLimit) || 0),
        isActive: form.isActive,
      })
      if (!parsed) throw new Error('VALIDATION')
      return customer ? customersApi.update(customer.id, parsed) : customersApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success(customer ? 'تم حفظ الزبون' : 'تمت إضافة الزبون')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ الزبون')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={customer ? `تعديل: ${customer.fullName}` : 'زبون جديد'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="اسم الزبون" required autoFocus
            value={form.fullName} onChange={(e) => set('fullName', e.target.value)} error={errors.fullName}
          />
          <Input
            label="رقم الهاتف" required ltr
            value={form.phone} onChange={(e) => set('phone', e.target.value)} error={errors.phone}
            hint="يُستعمل لربط حسابه في تطبيق الزبائن"
          />
          <Input
            label="دين سابق" type="number" min="0" step="0.01" ltr
            value={form.openingBalance} onChange={(e) => set('openingBalance', e.target.value)}
            error={errors.openingBalance}
            hint="دين قائم قبل استعمال النظام"
          />
          <Input
            label="سقف الدين" type="number" min="0" step="0.01" ltr
            value={form.creditLimit} onChange={(e) => set('creditLimit', e.target.value)}
            error={errors.creditLimit}
            hint="اتركه فارغاً لبلا سقف"
          />
        </div>

        <Input label="العنوان" value={form.address} onChange={(e) => set('address', e.target.value)} error={errors.address} />
        <Textarea label="ملاحظات" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />

        <Switch
          checked={form.isActive}
          onChange={(e) => set('isActive', e.target.checked)}
          label="زبون نشط"
          description="غير النشط لا يظهر في قائمة البيع الآجل"
        />
      </div>
    </Dialog>
  )
}

function PaymentDialog({
  customer, onClose,
}: { customer: CustomerListItem | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, validate, handleApiError } = useFormErrors()
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('CASH')
  const [toDrawer, setToDrawer] = useState(true)
  const [note, setNote] = useState('')

  useEffect(() => { setAmount(''); setNote(''); setToDrawer(true); setMethod('CASH') }, [customer])

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(customerPaymentSchema, {
        customerId: customer!.id,
        method,
        amount: toMinor(Number(amount) || 0),
        note: note.trim(),
        toDrawer,
      })
      if (!parsed) throw new Error('VALIDATION')
      return customersApi.pay(parsed)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success('سُجّل التسديد', `المتبقّي ${formatMoney(res.remainingBalance)}`)
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر تسجيل التسديد')
    },
  })

  return (
    <Dialog
      open={!!customer}
      onClose={onClose}
      size="sm"
      title="تسديد دين"
      description={customer ? `${customer.fullName} — الدين ${formatMoney(customer.balance)}` : undefined}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>تسجيل</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Input
          label="المبلغ" required type="number" min="0" step="0.01" ltr autoFocus
          value={amount} onChange={(e) => setAmount(e.target.value)} error={errors.amount} hint="دج"
        />
        <button
          type="button"
          onClick={() => setAmount(String(toMajor(customer?.balance ?? 0)))}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-hover"
        >
          الدين كاملاً
        </button>

        <Select
          label="طريقة التسديد"
          value={method} onChange={(e) => setMethod(e.target.value)}
          options={Object.entries(PAYMENT_METHODS)
            .filter(([k]) => k !== 'CREDIT')
            .map(([value, label]) => ({ value, label }))}
        />

        <Switch
          checked={toDrawer}
          onChange={(e) => setToDrawer(e.target.checked)}
          disabled={method !== 'CASH'}
          label="إضافة إلى درج الصندوق"
          description={method !== 'CASH' ? 'للنقد فقط' : 'يتطلّب وردية مفتوحة'}
        />

        <Textarea label="ملاحظة" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  )
}

function StatementDialog({ customerId, onClose }: { customerId: string | null; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['customers', 'statement', customerId],
    queryFn: () => customersApi.statement(customerId!),
    enabled: !!customerId,
  })

  return (
    <Dialog
      open={!!customerId}
      onClose={onClose}
      size="xl"
      title={data ? `كشف حساب: ${data.customer.fullName}` : 'كشف الحساب'}
      description={data ? `الرصيد ${formatMoney(data.balance)}` : undefined}
    >
      {data && (
        <div className="space-y-4">
          <Alert tone={data.balance > 0 ? 'warning' : 'success'}>
            {data.balance > 0
              ? <>مستحق على الزبون: <span className="font-semibold" dir="ltr">{formatMoney(data.balance)}</span></>
              : 'لا دين على هذا الزبون'}
          </Alert>

          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>التاريخ</Th>
                  <Th>البيان</Th>
                  <Th>المرجع</Th>
                  <Th align="end">دين</Th>
                  <Th align="end">تسديد</Th>
                  <Th align="end">الرصيد</Th>
                </tr>
              </Thead>
              <Tbody>
                {data.entries.map((e) => (
                  <Tr key={`${e.kind}-${e.id}`}>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(e.at)}</Td>
                    <Td>
                      <Badge tone={e.kind === 'payment' ? 'success' : e.kind === 'opening' ? 'neutral' : 'warning'}>
                        {e.kind === 'payment' ? 'تسديد' : e.kind === 'opening' ? 'رصيد سابق' : 'بيع آجل'}
                      </Badge>
                    </Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{e.reference}</Td>
                    <Td align="end" className="tabular-nums" dir="ltr">{e.debit > 0 ? formatMoney(e.debit) : '—'}</Td>
                    <Td align="end" className="tabular-nums text-success" dir="ltr">
                      {e.credit > 0 ? formatMoney(e.credit) : '—'}
                    </Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(e.balance)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {data.entries.length === 0 && (
              <EmptyState icon={<Wallet className="size-9" />} title="لا توجد حركات على هذا الزبون" />
            )}
          </TableWrapper>
        </div>
      )}
    </Dialog>
  )
}

export function CustomersPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.CUSTOMER_MANAGE)

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState<string | undefined>()
  const [withDebt, setWithDebt] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [paying, setPaying] = useState<CustomerListItem | null>(null)
  const [statementId, setStatementId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<CustomerListItem | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['customers', { page, search, withDebt }],
    queryFn: () => customersApi.list({ page, pageSize: 25, search, withDebt }),
    placeholderData: keepPreviousData,
  })
  const { data: totals } = useQuery({ queryKey: ['customers', 'total-debt'], queryFn: customersApi.totalDebt })
  const { data: editing } = useQuery({
    queryKey: ['customers', 'detail', editingId],
    queryFn: () => customersApi.get(editingId!),
    enabled: !!editingId,
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => customersApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('تم حذف الزبون')
      setDeleting(null)
    },
    onError: (err) => {
      toast.error('تعذّر الحذف', err instanceof Error ? err.message : undefined)
      setDeleting(null)
    },
  })

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">الزبائن</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} زبون` : 'جارٍ التحميل…'}
          </p>
        </div>
        {canManage && (
          <Button startIcon={<Plus className="size-4" />} onClick={() => { setEditingId(null); setFormOpen(true) }}>
            زبون جديد
          </Button>
        )}
      </div>

      {totals && totals.totalDebt > 0 && (
        <Card>
          <CardBody className="flex items-center gap-4">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-warning-subtle text-warning">
              <HandCoins className="size-5" />
            </span>
            <div>
              <p className="text-sm text-fg-muted">إجمالي الديون على الزبائن</p>
              <p className="mt-0.5 text-2xl font-bold text-fg" dir="ltr">{formatMoney(totals.totalDebt)}</p>
            </div>
            <Button variant="secondary" className="ms-auto" onClick={() => { setWithDebt(true); setPage(1) }}>
              عرض المدينين
            </Button>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <form
          className="flex flex-1 gap-2"
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim() || undefined); setPage(1) }}
        >
          <Input
            placeholder="ابحث بالاسم أو الهاتف…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            startIcon={<Search className="size-4" />}
            endIcon={
              searchInput ? (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearch(undefined); setPage(1) }}
                  className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                  aria-label="مسح"
                >
                  <X className="size-4" />
                </button>
              ) : undefined
            }
          />
          <Button type="submit" variant="secondary">بحث</Button>
        </form>
        <Button
          variant={withDebt ? 'primary' : 'secondary'}
          onClick={() => { setWithDebt((v) => !v); setPage(1) }}
        >
          {withDebt ? 'كل الزبائن' : 'المدينون فقط'}
        </Button>
      </div>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>الزبون</Th>
              <Th>الهاتف</Th>
              <Th align="center">الطلبات</Th>
              <Th align="end">الدين</Th>
              <Th align="end">السقف</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={6} cols={6} />
          ) : (
            <Tbody>
              {data?.items.map((c) => (
                <Tr key={c.id} className={cn(!c.isActive && 'opacity-55')}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-bg-subtle text-fg-subtle">
                        <Users className="size-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-fg">{c.fullName}</p>
                        {c.hasAppAccount && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-fg-subtle">
                            <Smartphone className="size-3" /> لديه حساب في التطبيق
                          </span>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td className="text-sm text-fg-muted" dir="ltr">{c.phone}</Td>
                  <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(c.orderCount)}</Td>
                  <Td align="end">
                    <span
                      className={cn('font-semibold tabular-nums', c.balance > 0 ? 'text-warning' : 'text-fg-muted')}
                      dir="ltr"
                    >
                      {formatMoney(c.balance)}
                    </span>
                  </Td>
                  <Td align="end" className="tabular-nums text-fg-subtle" dir="ltr">
                    {c.creditLimit > 0 ? formatMoney(c.creditLimit) : '—'}
                  </Td>
                  <Td align="center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="icon" title="كشف الحساب" onClick={() => setStatementId(c.id)}>
                        <Wallet className="size-4" />
                      </Button>
                      {canManage && c.balance > 0 && (
                        <Button variant="ghost" size="icon" title="تسديد" onClick={() => setPaying(c)}>
                          <HandCoins className="size-4" />
                        </Button>
                      )}
                      {canManage && (
                        <>
                          <Button
                            variant="ghost" size="icon" title="تعديل"
                            onClick={() => { setEditingId(c.id); setFormOpen(true) }}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" title="حذف"
                            className="text-danger hover:bg-danger-subtle"
                            onClick={() => setDeleting(c)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<Users className="size-10" />}
            title={withDebt ? 'لا يوجد مدينون' : search ? 'لا توجد نتائج' : 'لا يوجد زبائن'}
            description={
              withDebt ? 'لا دين على أحد حالياً'
                : 'أضف الزبائن الذين تبيع لهم آجلاً، أو يسجّلون عبر تطبيق الزبائن'
            }
            action={
              canManage && !withDebt && !search
                ? <Button onClick={() => { setEditingId(null); setFormOpen(true) }}>إضافة زبون</Button>
                : undefined
            }
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page} totalPages={data.totalPages} total={data.total} pageSize={data.pageSize}
            onPageChange={setPage}
          />
        )}
      </TableWrapper>

      <CustomerFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingId(null) }}
        customer={editingId ? (editing ?? null) : null}
      />
      <PaymentDialog customer={paying} onClose={() => setPaying(null)} />
      <StatementDialog customerId={statementId} onClose={() => setStatementId(null)} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeMutation.mutate(deleting.id)}
        loading={removeMutation.isPending}
        title="حذف الزبون"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{deleting?.fullName}</span>.
            <br />
            لا يمكن حذف زبون عليه دين — سجّل التسديد أولاً.
          </>
        }
      />
    </div>
  )
}
